import { useEffect, useRef, useState } from 'react';
import {
  Easing,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useDelayRender,
  useVideoConfig,
} from 'remotion';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const ZOOM_FACTOR = 4;
const ZOOM_DURATION = 1.5;
const MARKER_SIZE = 22;
const AUSTRIAN_BOUNDS: [[number, number], [number, number]] = [
  [9.53, 46.37],
  [17.16, 49.02],
];

type LonLat = [number, number];

type View = {
  center: LonLat;
  zoom: number;
};

const lerp = (from: number, to: number, t: number): number =>
  from + (to - from) * t;

type Props = {
  marker: LonLat;
  state?: string;
};

export const AustriaMap: React.FC<Props> = ({ marker, state }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { delayRender, continueRender } = useDelayRender();
  const containerRef = useRef<HTMLDivElement>(null);
  const [handle] = useState(() => delayRender('Loading Austria map...'));
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const [startView, setStartView] = useState<View | null>(null);
  const [markerPoint, setMarkerPoint] = useState<{ x: number; y: number } | null>(
    null,
  );

  const [markerLng, markerLat] = marker;
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    let cancelled = false;

    maplibregl.setWorkerUrl(
      URL.createObjectURL(
        new Blob(
          [
            `import "https://unpkg.com/maplibre-gl@${maplibregl.getVersion()}/dist/maplibre-gl-worker.mjs";`,
          ],
          { type: 'text/javascript' },
        ),
      ),
    );

    const mapInstance = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          austria: {
            type: 'geojson',
            data: staticFile('austria-states.geojson'),
          },
        },
        layers: [
          {
            id: 'austria-state-fill',
            type: 'fill',
            source: 'austria',
            filter: ['==', ['get', 'name'], ''] as maplibregl.FilterSpecification,
            paint: {
              'fill-color': '#fff',
            },
          },
          {
            id: 'austria-borders',
            type: 'line',
            source: 'austria',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#fff',
              'line-width': 1,
            },
          },
        ],
      },
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
      canvasContextAttributes: {
        preserveDrawingBuffer: true,
      },
    });

    mapInstance.on('load', () => {
      mapInstance.fitBounds(AUSTRIAN_BOUNDS, {
        padding: 6,
        animate: false,
        duration: 0,
      });
      mapInstance.once('idle', () => {
        if (cancelled) {
          return;
        }
        mapInstance.setFilter('austria-state-fill', [
          '==',
          ['get', 'name'],
          stateRef.current ?? '',
        ] as maplibregl.FilterSpecification);
        const center = mapInstance.getCenter();
        setStartView({
          center: [center.lng, center.lat],
          zoom: mapInstance.getZoom(),
        });
        setMap(mapInstance);
        continueRender(handle);
      });
    });

    return () => {
      cancelled = true;
      mapInstance.remove();
    };
  }, [handle, continueRender]);

  useEffect(() => {
    if (!map || !startView) {
      return;
    }

    const progress = interpolate(frame, [0, ZOOM_DURATION * fps], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.out(Easing.cubic),
    });

    const targetZoom = startView.zoom + Math.log2(ZOOM_FACTOR);
    map.jumpTo({
      center: [
        lerp(startView.center[0], markerLng, progress),
        lerp(startView.center[1], markerLat, progress),
      ],
      zoom: lerp(startView.zoom, targetZoom, progress),
    });
    map.triggerRepaint();

    const point = map.project([markerLng, markerLat]);
    setMarkerPoint({ x: point.x, y: point.y });
  }, [frame, fps, map, startView, markerLng, markerLat]);

  useEffect(() => {
    if (!map || !map.getLayer('austria-state-fill')) {
      return;
    }
    map.setFilter('austria-state-fill', [
      '==',
      ['get', 'name'],
      state ?? '',
    ] as maplibregl.FilterSpecification);
  }, [map, state]);

  const markerScale = spring({
    frame,
    fps,
    delay: ZOOM_DURATION * fps,
    config: { damping: 12, stiffness: 180, mass: 0.5 },
  });

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {markerPoint ? (
        <div
          style={{
            position: 'absolute',
            left: markerPoint.x,
            top: markerPoint.y,
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            marginLeft: -MARKER_SIZE / 2,
            marginTop: -MARKER_SIZE / 2,
            borderRadius: '50%',
            background: '#f2c400',
            opacity: Math.min(1, markerScale),
            transform: `scale(${markerScale})`,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </div>
  );
};
